"use client";

import { useState, useRef, useEffect } from "react";

type SearchableSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  emptyText?: string;
  allowCustom?: boolean;
};

export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Odaberi...",
  emptyText = "Nema rezultata",
  allowCustom = true,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState(value || "");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when value prop changes
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus input when dropdown opens
      setTimeout(() => inputRef.current?.focus(), 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if current value is in options
  const isValueInOptions = options.includes(value);
  const displayValue = isValueInOptions ? value : inputValue;

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setInputValue(selectedValue);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
  };

  const handleInputBlur = () => {
    // Delay to allow click events on dropdown items to fire first
    setTimeout(() => {
      if (!isOpen) return; // Already closed by click
      
      // If custom values are allowed and user typed something, use it
      if (allowCustom && inputValue.trim() && !isValueInOptions) {
        onChange(inputValue.trim());
      } else if (!isValueInOptions && inputValue.trim()) {
        // If value is not in options and not empty, try to find match or use custom
        const match = options.find(
          (opt) => opt.toLowerCase() === inputValue.toLowerCase()
        );
        if (match) {
          onChange(match);
          setInputValue(match);
        } else if (allowCustom) {
          onChange(inputValue.trim());
        } else {
          // Reset to previous value if custom not allowed
          setInputValue(value || "");
        }
      }
      setIsOpen(false);
      setSearchTerm("");
    }, 200);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && filteredOptions.length > 0) {
      handleSelect(filteredOptions[0]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const hasValue = value && value.trim() !== "";

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`w-full ${hasValue ? "pr-16 sm:pr-20" : "pr-10 sm:pr-12"} px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--card)] text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] transition`}
          placeholder={placeholder}
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm(displayValue);
          }}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
        />
        {hasValue && (
          <button
            type="button"
            className="absolute right-8 sm:right-10 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
              setInputValue("");
              setSearchTerm("");
            }}
            title="Očisti"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              setSearchTerm(displayValue);
              setTimeout(() => inputRef.current?.focus(), 0);
            }
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--card)] border border-[var(--border)] rounded-lg sm:rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            <ul className="py-1">
              {filteredOptions.map((option) => (
                <li
                  key={option}
                  className={`px-3 py-2 text-xs sm:text-sm md:text-base cursor-pointer hover:bg-[var(--accent)]/10 transition ${
                    value === option ? "bg-[var(--accent)]/20 font-semibold" : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(option);
                  }}
                >
                  {option}
                </li>
              ))}
            </ul>
          ) : allowCustom && searchTerm.trim() ? (
            <div className="px-3 py-2 text-xs sm:text-sm text-[var(--muted)]">
              <button
                type="button"
                className="w-full text-left hover:text-[var(--text)] transition"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(searchTerm.trim());
                }}
              >
                Koristi "{searchTerm.trim()}"
              </button>
            </div>
          ) : (
            <div className="px-3 py-2 text-xs sm:text-sm text-[var(--muted)]">
              {emptyText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

